import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { PageHeader, Panel } from "@/components/shared";
import { api, errMsg, salaryText } from "@/lib/api";

export default function Autopilot() {
  const [d, setD] = useState(null);
  const [minScore, setMinScore] = useState(60);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/autopilot").then((r) => { setD(r.data); setMinScore(r.data.settings.min_score ?? 60); });
  useEffect(() => { load(); }, []);
  const save = async (enabled) => {
    try { await api.put("/autopilot", { enabled, min_score: minScore }); toast.success("Autopilot settings saved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/autopilot/run"); toast.success(`${data.added} new suggestion(s) from ${data.evaluated} openings`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dismiss = async (id) => { await api.post(`/autopilot/recommendations/${id}/dismiss`); load(); };
  if (!d) return null;
  return (
    <div>
      <PageHeader eyebrow="Suggests, never submits" title="Career Autopilot" />
      <div className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900" data-testid="autopilot-safeguard">
        <ShieldCheck className="h-5 w-5 shrink-0" /><p>Autopilot scans verified openings daily and suggests strong fits. It never applies on your behalf — every application requires your explicit approval.</p>
      </div>
      {!d.entitled && <Panel className="mb-6" testid="autopilot-upgrade"><p className="text-sm">Career Autopilot is included from the Professional plan. <Link to="/app/billing" className="font-medium text-primary">Upgrade</Link></p></Panel>}
      <Panel className="mb-6" testid="autopilot-settings">
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-3 text-sm font-medium"><Switch checked={!!d.settings.enabled} disabled={!d.entitled} onCheckedChange={save} data-testid="autopilot-enabled-switch" />Daily autopilot</label>
          <div className="min-w-[220px] flex-1"><p className="mb-2 text-sm">Minimum fit score: <b className="font-mono">{minScore}</b></p><Slider min={30} max={95} step={5} value={[minScore]} onValueChange={([v]) => setMinScore(v)} onValueCommit={() => save(!!d.settings.enabled)} disabled={!d.entitled} data-testid="autopilot-min-score-slider" /></div>
          <Button onClick={runNow} disabled={!d.entitled || busy} data-testid="autopilot-run-button" className="rounded-full">{busy ? "Scanning…" : "Run now"}</Button>
        </div>
      </Panel>
      <div className="space-y-3" data-testid="autopilot-recommendations">
        {d.recommendations.length === 0 && <p className="text-sm text-muted-foreground">No open suggestions.</p>}
        {d.recommendations.map((r) => (
          <div key={r.rec_id} className="lift flex flex-col gap-3 rounded-2xl border bg-white p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`recommendation-${r.rec_id}`}>
            <div><p className="font-semibold">{r.job_snapshot.title}</p><p className="text-sm text-muted-foreground">{r.job_snapshot.employer} · {r.job_snapshot.location} · {salaryText(r.job_snapshot)}</p>
              {r.flags?.length > 0 && <p className="mt-1 text-xs text-amber-700">{r.flags[0]}</p>}</div>
            <div className="flex items-center gap-2"><span className="mr-2 font-mono text-lg font-bold">{r.score}</span>
              <Button asChild size="sm" data-testid={`recommendation-review-${r.rec_id}`}><Link to={`/app/jobs/${r.job_id}`}>Review</Link></Button>
              <Button size="sm" variant="ghost" onClick={() => dismiss(r.rec_id)} data-testid={`recommendation-dismiss-${r.rec_id}`}>Dismiss</Button></div>
          </div>
        ))}
      </div>
    </div>
  );
}
